import React, { useState } from 'react';
import { FaTrashAlt, FaPenSquare, FaPaperclip } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import usePayments from '../hooks/usePayments';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableRow,
  TableData,
  EditButton,
  DeleteButton,
} from '../styles/ComponentStyles';
import PaginationControl from './PaginationControl';
import { attachmentCount } from '../utils/paymentAttachments';
import { deletePaymentEverywhereWarning } from '../utils/paymentLink';

const confirmBtnStyle = (color) => ({
  background: 'none',
  border: 'none',
  color,
  cursor: 'pointer',
  padding: '0 6px',
});

const PaymentsTableMain = ({
    refreshKey,
    setRefreshKey,
    handleFlashMessage,
    handleDelete,
    currentPage,
    currentPageLimit,
    onPageChange,
    onPageLimitChange,
    period,
    startDate,
    endDate
  }) => {
  const navigate = useNavigate();
  const { payments, totalPages, loading, error } = usePayments(currentPage, currentPageLimit, refreshKey, period, startDate, endDate);

  const handleEdit = (paymentId) => navigate(`/edit-payment/${paymentId}`);

  // A delete from this list ends the payment itself rather than one parent's
  // reference to it, so it asks first and names the records that lose it too.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const confirmDelete = async (paymentId) => {
    setDeleteBusy(true);
    try {
      await handleDelete(paymentId);
    } finally {
      setDeleteBusy(false);
      setPendingDeleteId(null);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading payments.</div>;

  return (
    <TableWrapper>
      <h2>Payments List</h2>
      <Table>
        <thead>
          <tr>
            <TableHeader>Biller</TableHeader>
            <TableHeader>Payment Date</TableHeader>
            <TableHeader>Amount</TableHeader>
            <TableHeader>Payment Method</TableHeader>
            <TableHeader>Remarks</TableHeader>
            <TableHeader>Receipts</TableHeader>
            <TableHeader>Actions</TableHeader>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <TableRow key={payment.id}>
              <TableData>{payment.biller}</TableData>
              <TableData>{payment.payment_date}</TableData>
              <TableData>{payment.payment_amount}</TableData>
              <TableData>{payment.payment_method}</TableData>
              <TableData>{payment.remarks}</TableData>
              {/* A paperclip with a count, so the list shows at a glance which
                  payments have a receipt behind them. Managing them is a click
                  away, on the edit page. */}
              <TableData>
                {attachmentCount(payment) > 0 ? (
                  <span
                    title={`${attachmentCount(payment)} attachment${attachmentCount(payment) === 1 ? '' : 's'}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <FaPaperclip /> {attachmentCount(payment)}
                  </span>
                ) : (
                  <span style={{ color: '#888' }}>—</span>
                )}
              </TableData>
              <TableData>
                {pendingDeleteId === payment.id ? (
                  <span>
                    <span style={{ display: 'block', fontSize: '0.8rem', marginBottom: 4 }}>
                      {deletePaymentEverywhereWarning(payment)}
                    </span>
                    <button
                      type="button"
                      onClick={() => confirmDelete(payment.id)}
                      disabled={deleteBusy}
                      style={confirmBtnStyle('#f44336')}
                    >
                      {deleteBusy ? 'Deleting...' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteId(null)}
                      disabled={deleteBusy}
                      style={confirmBtnStyle('#888')}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <>
                    <EditButton onClick={() => handleEdit(payment.id)}>
                      <FaPenSquare />
                    </EditButton>
                    <DeleteButton onClick={() => setPendingDeleteId(payment.id)}>
                      <FaTrashAlt />
                    </DeleteButton>
                  </>
                )}
              </TableData>
            </TableRow>
          ))}
        </tbody>
      </Table>
      <PaginationControl
        currentPage={currentPage}
        currentPageLimit={currentPageLimit}
        totalPages={totalPages}
        handlePageChange={onPageChange}
        handlePageLimitChange={onPageLimitChange}
      />
    </TableWrapper>
  );
};

export default PaymentsTableMain;
